import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';

const KEY = 'equipr_last_activity';
const IDLE_MS = 60 * 60 * 1000;
const GRACE_S = 60;
const EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];

const lastActivity = () => Number(localStorage.getItem(KEY)) || Date.now();
const markActive = () => localStorage.setItem(KEY, String(Date.now()));

// Office-only inactivity guard. Shared across tabs via localStorage.
export const IdleTimeout = () => {
  const { logout, refreshNow } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(GRACE_S);
  const openRef = useRef(false);
  const lastWrite = useRef(0);

  useEffect(() => {
    markActive();
    const onActivity = () => {
      if (openRef.current) return;
      const now = Date.now();
      if (now - lastWrite.current > 15000) { lastWrite.current = now; markActive(); }
    };
    EVENTS.forEach(e => window.addEventListener(e, onActivity, { passive: true }));

    const signOut = async () => {
      try { await authApi.sessionTimeout(); } catch { /* token may already be gone */ }
      logout();
      toast.info('You were signed out after 60 minutes of inactivity');
      navigate('/login', { replace: true });
    };

    const tick = setInterval(() => {
      if (!localStorage.getItem('equipr_token')) { navigate('/login', { replace: true }); return; }
      const idle = Date.now() - lastActivity();
      if (idle >= IDLE_MS + GRACE_S * 1000) { clearInterval(tick); signOut(); return; }
      if (idle >= IDLE_MS) {
        openRef.current = true;
        setOpen(true);
        setSecondsLeft(Math.max(0, Math.ceil((IDLE_MS + GRACE_S * 1000 - idle) / 1000)));
      } else if (openRef.current) {
        openRef.current = false;
        setOpen(false);
      }
    }, 1000);

    return () => {
      EVENTS.forEach(e => window.removeEventListener(e, onActivity));
      clearInterval(tick);
    };
  }, [logout, navigate]);

  const stay = () => {
    markActive();
    openRef.current = false;
    setOpen(false);
    refreshNow();
  };

  const signOutNow = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) stay(); }}>
      <DialogContent className="sm:max-w-sm" data-testid="idle-timeout-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Clock className="w-5 h-5 text-[#D97706]" />Still there?</DialogTitle>
          <DialogDescription>
            You've been inactive for 60 minutes. For security you'll be signed out in{' '}
            <span className="font-mono font-bold text-foreground" data-testid="idle-timeout-countdown">{secondsLeft}s</span>.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={signOutNow} data-testid="idle-timeout-signout">Sign out</Button>
          <Button onClick={stay} className="btn-industrial" data-testid="idle-timeout-stay">Stay signed in</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
